import { useQuery } from "@tanstack/react-query";
import { useContext, useState } from "react";

import { queries, parseSearchResults, GraphQLAPI } from "@examspark/api";
import { itemsGql } from "@examspark/common-app-search";
import {
  Maybe,
  SearchAggsInput,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  SearchableResourceSortInput,
  SearchableResourceSortableFields,
  SearchableSortDirection,
} from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useAuthorizationContext } from "../../../../hooks/useAuthorizationContext";

type SearchItemsParams = {
  /** Flag to allow exact matches to an ID even if outside the `filter` */
  allowResultsOutsideFilterWithId?: boolean;
  exclude?: string[];
  random?: boolean;
  /** Make debugging easier to know who is calling this. */
  label?: string;
  /** Set to true to return item data fields. Otherwise, only aggregate counts are returned. */
  items?: boolean;
  limit?: number;
  filter?: {
    query?: string;
    queryId?: string;
    language?: string;
    document?: string;
    scope?: string;
    scopes?: string[];
    interactionTypes?: string[];
    objectiveIds?: string[];
    contextIds?: string[];
  };
};

export const useExamBuilderSearch = () => {
  const authContext = useAuthorizationContext();
  const [state] = useContext(NewActivityContext);
  const [isLoading, setIsLoading] = useState(false);

  const searchItems = async (params?: SearchItemsParams) => {
    console.log("searchItems", params, state);
    const aggsNew = getExamBuilderSearchAggs({
      document: state.document,
    });

    const filter: SearchableResourceFilterInput = {
      sk: {
        eq: "item#v0",
      },
      language: {
        exists: true,
      },
      and: [
        {
          interaction: {
            ne: "gridded",
          },
        },
      ],
    };

    filter.and = filter.and ?? [];

    if (params?.filter?.queryId) {
      if (!filter.or) filter.or = [];
      filter.or.push({
        idInc: {
          eq: params.filter.queryId,
        },
      });
    }

    if (
      !params?.filter?.queryId ||
      (!!params.filter.queryId && !params.allowResultsOutsideFilterWithId)
    ) {
      // console.log("adding all", state, state.document, params?.exclude);
      if (
        state.document &&
        state.document.length &&
        !params?.exclude?.includes("document")
      ) {
        filter.and.push({
          objectiveScope: {
            wildcard: state.document.replace("document#", "") + "*",
          },
        });
      }

      if (
        state.sections &&
        state.sections.length &&
        !params?.exclude?.includes("section")
      ) {
        filter.and.push({
          or: state.sections.map((section) => ({
            objectiveScope: {
              wildcard: section.replace("section#", "") + "*",
            },
          })),
        });
      }

      // we might want to specify the search filter, for example when we're
      // building the exam, we query per objective.
      if (params?.filter) {
        if (params?.filter.query) {
          if (!filter.or) filter.or = [];
          filter.or.push({
            name: {
              match: params.filter.query,
            },
          });
        }

        if (params?.filter.language) {
          filter.language = {
            eq: params.filter.language,
          };
        }

        if (params?.filter.contextIds?.length) {
          if (params.filter.contextIds.includes("-1")) {
            filter.and.push({
              or: params.filter.contextIds.map((v) => {
                return {
                  contextIds: {
                    exists: false,
                  },
                };
              }),
            });
          } else {
            filter.and.push({
              or: params.filter.contextIds.map((v) => {
                return {
                  contextIds: {
                    eq: v,
                  },
                };
              }),
            });
          }
        }

        if (params?.filter.objectiveIds) {
          filter.and.push({
            or: params.filter.objectiveIds.map((obj) => ({
              alignmentIds: {
                eq: obj,
              },
            })),
          });
        }

        if (params?.filter.document) {
          filter.objectiveDocument = {
            eq: params.filter.document,
          };
        }

        if (params?.filter.scope) {
          filter.objectiveScope = {
            eq: params.filter.scope,
          };
        }

        if (params?.filter.interactionTypes) {
          filter.and.push({
            or: params.filter.interactionTypes.map((v) => {
              return {
                interaction: {
                  eq: v,
                },
              };
            }),
          });
        }
      } else {
        if (state.language && !params?.exclude?.includes("language")) {
          filter.language = {
            eq: state.language,
          };
        }

        if (state.passages.length > 0) {
          const allPassageItems = state.isIncludeAllPassageItemsEnabled;
          const nonPassageItems = !state.isIncludeOnlyPassageItemsEnabled;

          const contextFilter = !params?.exclude?.includes("passages")
            ? state.passages.map((v) => {
                return {
                  contextIds: {
                    eq: v.id,
                  },
                };
              })
            : [];

          const objectiveFilter = !params?.exclude?.includes("objectives")
            ? state.objectives.map((obj) => ({
                alignmentIds: {
                  eq: obj.id,
                },
              }))
            : [];

          if (allPassageItems && nonPassageItems) {
            filter.and.push({
              or: [...contextFilter, ...objectiveFilter],
            });
          } else if (allPassageItems && !nonPassageItems) {
            filter.and.push({
              or: contextFilter,
            });
          } else if (!allPassageItems && nonPassageItems) {
            filter.and.push({
              or: objectiveFilter,
            });
          } else {
            filter.and.push(
              {
                or: [...contextFilter],
              },
              {
                or: [...objectiveFilter],
              },
            );
          }
        } else {
          if (
            state.passages.length > 0 &&
            state.isIncludeOnlyPassageItemsEnabled &&
            !params?.exclude?.includes("passages")
          ) {
            filter.and.push({
              or: state.passages.map((v) => {
                return {
                  contextIds: {
                    eq: v.id,
                  },
                };
              }),
            });
          }

          if (state.objectives && !params?.exclude?.includes("objectives")) {
            const expandSearchToIncludeNonPassageItems =
              !state.isIncludeOnlyPassageItemsEnabled;

            if (!expandSearchToIncludeNonPassageItems) {
              filter.and.push({
                or: state.objectives.map((obj) => ({
                  alignmentIds: {
                    eq: obj.id,
                  },
                })),
              });
            }
          }
        }

        if (
          state.interactionTypes &&
          !params?.exclude?.includes("interactionTypes")
        ) {
          filter.and.push({
            or: state.interactionTypes.map((v) => {
              return {
                interaction: {
                  eq: v,
                },
              };
            }),
          });
        }
      }
    }

    const sort: SearchableResourceSortInput[] = [];
    if (params?.random) {
      sort.push({
        field: SearchableResourceSortableFields.EsRandom,
        direction: SearchableSortDirection.Desc,
      });
    }

    const limit = params?.limit;
    // console.log(`[${params?.label}] about to search`, printJSON(filter));
    setIsLoading(true);
    const results = await GraphQLAPI.query<{
      search: SearchableResourceConnectionNew;
    }>({
      query: queries.search.search({
        label: "SearchLicensedItems",
        fields: params?.items ? itemsGql : undefined,
      }),
      variables: {
        sort,
        filter,
        limit,
        aggsNew,
        authorizationContext: authContext,
      },
    });

    setIsLoading(false);
    return parseSearchResults(results.data?.search);
  };

  return {
    isLoading,
    searchItems,
  };
};

// wrap the existing searchItems function in a useQuery hook
export const useQuerySearchItems = (filters: {
  query?: string;
  queryId?: string;
  objectiveIds?: string[];
  language?: string;
  interactionTypes?: string[];
  contextIds?: string[];
}) => {
  const search = useExamBuilderSearch();

  return useQuery(["search-items", filters], {
    keepPreviousData: true,
    queryFn: async () => {
      return await search.searchItems({
        items: true,
        allowResultsOutsideFilterWithId: true,
        limit: 200,
        filter: filters,
      });
    },
  });
};

export const getExamBuilderSearchAggs = (state?: {
  document?: Maybe<string>;
}) => {
  const size = 30; // default is 10, which cuts off several courses
  const aggsNew: SearchAggsInput[] = [
    {
      name: "objectiveScope",
      type: "terms",
      field: "objectiveScope",
      size,
      include: state?.document
        ? `${state.document.split("#")[1]}.*`
        : undefined,
    },
    {
      name: "objectiveAbbreviation",
      type: "terms",
      field: "objectiveAbbreviation",
      size,
    },
    {
      name: "subject",
      type: "terms",
      field: "subject",
      size,
    },
    {
      name: "language",
      type: "terms",
      field: "language",
      size,
    },
    {
      name: "alignmentIds",
      type: "terms",
      field: "alignmentIds",
      size,
    },
    {
      name: "objectiveDocument",
      type: "terms",
      field: "objectiveDocument",
      size,
    },
    {
      name: "grade",
      type: "terms",
      field: "grade",
      size,
    },
    {
      name: "interaction",
      type: "terms",
      field: "interaction",
      size,
    },
    {
      name: "contextIds",
      type: "terms",
      field: "contextIds",
      size: 250,
    },
    {
      name: "objectiveId",
      type: "terms",
      field: "objectiveId",
      size: 250,
    },
  ];

  return aggsNew;
};

export const getFilterForNewActivity = (params: {
  filter: SearchItemsParams["filter"];
}) => {
  const filter: SearchableResourceFilterInput = {};

  filter.and = filter.and ?? [];

  if (params?.filter?.queryId) {
    if (!filter.or) filter.or = [];
    filter.or.push({
      idInc: {
        eq: params.filter.queryId,
      },
    });
  }

  if (params?.filter?.query) {
    if (!filter.or) filter.or = [];
    filter.or.push({
      name: {
        match: params.filter.query,
      },
    });
  }

  if (params?.filter?.language) {
    filter.language = {
      eq: params.filter.language,
    };
  }

  if (params?.filter?.contextIds?.length) {
    if (params.filter.contextIds.includes("-1")) {
      filter.and.push({
        or: params.filter.contextIds.map((v) => {
          return {
            contextIds: {
              exists: false,
            },
          };
        }),
      });
    } else {
      filter.and.push({
        or: params.filter.contextIds.map((v) => {
          return {
            contextIds: {
              eq: v,
            },
          };
        }),
      });
    }
  }

  if (params?.filter?.objectiveIds) {
    filter.and.push({
      or: params.filter.objectiveIds.map((obj) => ({
        alignmentIds: {
          eq: obj,
        },
      })),
    });
  }

  if (params?.filter?.document) {
    filter.objectiveDocument = {
      eq: params.filter.document,
    };
  }

  if (params?.filter?.scope) {
    filter.objectiveScope = {
      eq: params.filter.scope,
    };
  }

  if (params?.filter?.interactionTypes) {
    filter.and.push({
      or: params.filter.interactionTypes.map((v) => {
        return {
          interaction: {
            eq: v,
          },
        };
      }),
    });
  }

  return filter;
};
